import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {DateValidators} from '@aviary/forms/fields/date-form-field/date.form-validators';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormBuilder} from '@aviary/forms/extensions/extended-form-builder/extended-form-builder';
import {ComponentLibSectionComponent} from '@aviary/components/component-lib/section/component-lib-section.component';
import {MatAccordion, MatExpansionPanel, MatExpansionPanelDescription, MatExpansionPanelHeader, MatExpansionPanelTitle} from '@angular/material/expansion';
import {MatIcon} from '@angular/material/icon';
import {NgTemplateOutlet} from '@angular/common';
import {ActionButtonsComponent} from '@aviary/components/action-buttons/action-buttons.component';
import {MatCard, MatCardActions, MatCardContent, MatCardSubtitle, MatCardTitle, MatCardTitleGroup} from '@angular/material/card';
import {MatChip, MatChipListbox, MatChipSet} from '@angular/material/chips';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatInput} from '@angular/material/input';
import {DateRangeFormFieldComponent} from '@aviary/forms/fields/date-range-form-field/date-range-form-field.component';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {NumberFormFieldComponent} from '@aviary/forms/fields/number-form-field/number-form-field.component';
import {MobileBarcodeScannerComponent} from '@aviary/components/mobile-barcode-scanner/mobile-barcode-scanner.component';
import {FormRowComponent} from '@aviary/forms/fields/form-row/form-row.component';
import {ComponentLibButtonsComponent} from '@aviary/components/component-lib/buttons/component-lib-buttons.component';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';
import {ElevatedButtonComponent} from '@aviary/buttons/elevated-button/elevated-button.component';
import {ComponentLibColorGridComponent} from '@aviary/components/component-lib/color-grid/component-lib-color-grid.component';
import {MX_COLOR_ALIAS_NAMES} from '@styles/theming/generator/outputs/theme-metadata.generated';
import {ComponentLibSnackBarsComponent} from '@aviary/components/component-lib/snack-bars/component-lib-snack-bars.component';
import {ComponentLibBadgesComponent} from '@aviary/components/component-lib/badges/component-lib-badges.component';
import {ComponentLibCheckboxesComponent} from '@aviary/components/component-lib/checkboxes/component-lib-checkboxes.component';
import {StatusChipComponent} from '@aviary/components/status-chip/status-chip.component';
import {CIRCULATION_ITEM_STATUS_CHIP_COLORS, DISPLAYABLE_CIRCULATION_ITEM_STATUSES} from '@store/catalog/catalog.types';
import {DISPLAYABLE_HOLD_STATUSES, HOLD_STATUS_CHIP_COLORS} from '@store/holds/holds.types';
import {DISPLAYABLE_ITEM_REQUEST_STATUS, ITEM_REQUEST_STATUS_CHIP_COLORS} from '@store/item-requests/item-requests.types';
import {CHECKOUT_CLAIM_TYPE_CHIP_COLORS, DISPLAYABLE_CHECKOUT_CLAIM_TYPE} from '@store/checkouts/checkouts.types';
import {MatDivider} from '@angular/material/divider';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';
import {ComponentLibTextareaFormFieldsComponent} from '@aviary/components/component-lib/textarea-form-fields/component-lib-textarea-form-fields.component';

@Component({
  selector: 'px-component-lib',
  host: {
    class: 'block bg-surface2'
  },
  templateUrl: './component-lib.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ActionButtonsComponent, ComponentLibSectionComponent, DateRangeFormFieldComponent, FormRowComponent, MatAccordion, MatCard,
    MatCardTitle, MatCardSubtitle, MatCardTitleGroup, MatCardContent, MatCardActions, MatChipSet, MatChip, MatDatepickerToggle, MatDatepicker,
    MatDatepickerInput, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription, MatFormField, MatIcon, MatInput,
    MatLabel, MatSlideToggle, NgTemplateOutlet, NumberFormFieldComponent, MobileBarcodeScannerComponent, ComponentLibButtonsComponent, TextButtonComponent,
    ElevatedButtonComponent, ComponentLibColorGridComponent, ComponentLibSnackBarsComponent, ComponentLibBadgesComponent, ComponentLibCheckboxesComponent,
    StatusChipComponent, MatDivider, IconButtonComponent, MatChipListbox, ComponentLibTextareaFormFieldsComponent
  ]
})
export class ComponentLibComponent {
  public allSlideTogglesDisabled: boolean = false;
  public allSlideTogglesChecked: boolean = false;
  public currentTheme: 'light' | 'dark' = 'light';
  public readonly colorAliasNames: readonly string[] = MX_COLOR_ALIAS_NAMES;
  public readonly CIRCULATION_ITEM_STATUS_CHIP_COLORS = CIRCULATION_ITEM_STATUS_CHIP_COLORS;
  public readonly DISPLAYABLE_CIRCULATION_ITEM_STATUSES = DISPLAYABLE_CIRCULATION_ITEM_STATUSES;
  public readonly HOLD_STATUS_CHIP_COLORS = HOLD_STATUS_CHIP_COLORS;
  public readonly DISPLAYABLE_HOLD_STATUSES = DISPLAYABLE_HOLD_STATUSES;
  public readonly ITEM_REQUEST_STATUS_CHIP_COLORS = ITEM_REQUEST_STATUS_CHIP_COLORS;
  public readonly DISPLAYABLE_ITEM_REQUEST_STATUS = DISPLAYABLE_ITEM_REQUEST_STATUS;
  public readonly CHECKOUT_CLAIM_TYPE_CHIP_COLORS = CHECKOUT_CLAIM_TYPE_CHIP_COLORS;
  public readonly DISPLAYABLE_CHECKOUT_CLAIM_TYPE = DISPLAYABLE_CHECKOUT_CLAIM_TYPE;

  private readonly formBuilder: ExtendedFormBuilder = inject(ExtendedFormBuilder);

  public readonly dateRangeFormGroup: ExtendedFormGroup = this.formBuilder.nonNullable.extendedGroup({
    dateRange: ['', [DateValidators.notPastDate]] // IDEA:  Add checkboxes to the template to dynamically change the validators for demo/test purposes.
  });

  public readonly numbersFormGroup: ExtendedFormGroup = this.formBuilder.nonNullable.extendedGroup({
    integerNumber: ['', []],
    decimalNumber: ['', []]
  });

  public toggleDarkTheme(): void {
    this.currentTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
    document.body.classList.toggle('dark-mode');
  }
}
